/**
 * Floating banner shown when admin is impersonating a user.
 * Always visible to remind the admin they're viewing as someone else.
 */

import { X, Eye, User } from 'lucide-react';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { motion, AnimatePresence } from 'framer-motion';

export function ImpersonationBanner() {
  const { isImpersonating, impersonatedUser, stopImpersonation } = useImpersonation();

  return (
    <AnimatePresence>
      {isImpersonating && impersonatedUser && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg pt-safe pointer-events-auto"
        >
          <div className="max-w-screen-xl mx-auto px-4 py-2 flex items-center justify-between gap-4 pointer-events-auto">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
                <Eye className="w-4 h-4" />
                <span className="text-sm font-medium">Admin View Mode</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Avatar className="w-6 h-6 ring-2 ring-white/30">
                  <AvatarImage src={impersonatedUser.avatar_url || undefined} />
                  <AvatarFallback className="bg-white/20 text-white text-xs">
                    {impersonatedUser.display_name?.[0]?.toUpperCase() || <User className="w-3 h-3" />}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold leading-tight">
                    {impersonatedUser.display_name || 'Anonymous'}
                  </span>
                  <span className="text-xs text-white/70 leading-tight">
                    {impersonatedUser.email}
                  </span>
                </div>
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                stopImpersonation();
              }}
              className="bg-white/20 hover:bg-white/30 active:bg-white/40 text-white gap-2 pointer-events-auto touch-manipulation"
            >
              <X className="w-4 h-4" />
              Exit
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
